import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Backdrop,
  Fade,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  SxProps,
  Tooltip,
} from '@mui/material';
import ExpandIcon from '@mui/icons-material/Expand';
import { GENERAL_FONT, MHColours } from '../../constants/theme';
import { IDailyStatistics, IDiaryEntry } from '../../lib/interfaces';
import {
  getChartsStatisticsByMonths,
  getStatisticsByDays,
  getStatisticsByMonthsOrWeeks,
} from '../../lib/helpers/diary';
import LineChart from './LineChart';
import {
  getDatesBetweenTwoDates,
  getMonthNamesBetweenDates,
} from '../../lib/helpers/date';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1250px',
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  p: 4,
  borderRadius: '5px',
};

const ChartsContainer = styled.div`
  height: 100%;
  width: 100%;
  margin-top: 12px;
`;

const chartTypePickerSx: SxProps = {
  width: '100%',
  maxWidth: '100%',
  minWidth: '100px',
  '& .MuiFormLabel-root': {
    color: MHColours.mhGreen,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: `3px solid${MHColours.mhGreen}`,
  },
  '& .MuiInputLabel-root': {
    color: MHColours.mhGreen,
  },
};

const chartTypePickerLabelSx: SxProps = {
  color: `${MHColours.mhGreen} !important`,
};

export interface ChartsProps {
  diaryEntries: IDiaryEntry[];
  dateFrom: string;
  dateTo: string;
}

const Charts = (props: ChartsProps) => {
  const { diaryEntries, dateFrom, dateTo } = props;
  const [statisticsByMonthsOrWeeks, setStatisticsByMonthsOrWeeks] =
    useState<IDailyStatistics>();
  const [statisticsByDays, setStatisticsByDays] = useState<IDailyStatistics>();
  const [dailyLabels, setDaylyLabels] = useState<string[]>([]);
  const [monthlyLabels, setMonthlyLabels] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = React.useState(false);

  const { t } = useTranslation();

  const [chartType, setChartType] = useState<string>('month');

  useEffect(() => {
    loadMonthlyStatistics();
    loadDailyStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the loaders are recreated every render; recompute only when diaryEntries changes
  }, [diaryEntries]);

  const handleChange = (event: SelectChangeEvent) => {
    setChartType(event.target.value);
  };

  const loadMonthlyStatistics = () => {
    const result = getStatisticsByMonthsOrWeeks(diaryEntries);
    const chartMonthData = getChartsStatisticsByMonths(result);

    setStatisticsByMonthsOrWeeks(chartMonthData.statistics);

    const monthLabels = getMonthNamesBetweenDates(dateFrom, dateTo);
    setMonthlyLabels(monthLabels);
  };

  const loadDailyStatistics = () => {
    const dailyStatistics = getStatisticsByDays(diaryEntries, dateFrom, dateTo);
    setStatisticsByDays(dailyStatistics);
    const newLabels = getDatesBetweenTwoDates(dateFrom, dateTo);
    setDaylyLabels(newLabels);
  };

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);

  const expandButtonStyle = {
    borderRadius: '50%',
    backgroundColor: MHColours.mhGreen,
    fontFamily: GENERAL_FONT,
    color: 'white',
  };

  return (
    <>
      <ChartsContainer>
        <Grid container>
          <Grid item xs={6}>
            <Box sx={{ width: 150 }}>
              <FormControl fullWidth>
                <InputLabel
                  id="demo-simple-select-label"
                  sx={chartTypePickerLabelSx}
                >
                  {' '}
                  {t('chartType')}
                </InputLabel>
                <Select
                  sx={chartTypePickerSx}
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={chartType}
                  label="Chart type"
                  onChange={handleChange}
                >
                  <MenuItem value="month">{t('monthly')}</MenuItem>
                  <MenuItem value="day">{t('daily')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Tooltip title={t('expand')}>
              <IconButton
                onClick={handleModalOpen}
                sx={{ float: 'right' }}
                style={expandButtonStyle}
                aria-label="delete"
                size="large"
              >
                <ExpandIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <div style={{ marginTop: '12px' }}>
          <LineChart
            chartStatistics={
              chartType === 'month'
                ? statisticsByMonthsOrWeeks
                : statisticsByDays
            }
            labels={chartType === 'month' ? monthlyLabels : dailyLabels}
            chartType={chartType}
          />
        </div>
      </ChartsContainer>
      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={modalOpen}>
          <Box sx={modalStyle}>
            <LineChart
              chartStatistics={
                chartType === 'month'
                  ? statisticsByMonthsOrWeeks
                  : statisticsByDays
              }
              labels={chartType === 'month' ? monthlyLabels : dailyLabels}
              chartType={chartType}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default Charts;
